import axios from 'axios';
import apiClient from './client'
import { DeviceInstance } from '../types'

export const getAvailableDevices = async (projectId: number, electricalPanelId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}`;
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching available devices:', error);
    return [];
  }
};

export const getAvailableCables = async (projectId: number, electricalPanelId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}&device_type=cable`;
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching available cables:', error);
    return [];
  }
};

export const getProjectDevices = async (projectId: number, electricalPanelId?: number, deviceType?: string, specificationId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}`;
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    if (deviceType) {
      url += `&device_type=${deviceType}`;
    }
    if (specificationId) {
      url += `&spec=${specificationId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching project devices:', error);
    return [];
  }
};

// New function for cable instances
export const getProjectCableInstances = async (projectId: number): Promise<DeviceInstance[]> => {
  try {
    const response = await apiClient.get(`/api/device_instances?project_id=${projectId}&device_type=cable`);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching project cable instances:', error);
    return [];
  }
};

export const updateDeviceUsage = async (deviceInstanceId: number, used: boolean): Promise<any> => {
  try {
    const response = await apiClient.put(`/api/device_instances/${deviceInstanceId}/usage`, { used }) as any;
    return response;
  } catch (error) {
    console.error('❌ Error updating device usage:', error);
    throw error;
  }
};

export const createDeviceInstance = async (deviceData: {
  specification_id: number;
  project_id: number;
  electrical_panel_id: number;
  position_label?: string;
  description?: string;
  knx_address?: string;
}): Promise<DeviceInstance> => {
  const response = await apiClient.post('/api/device_instances', deviceData);
  return response.data;
};

export const updateDeviceInstance = async (
  id: number,
  updates: {
    position_label?: string | null;
    description?: string | null;
    knx_address?: string | null;
    electrical_panel_id?: number;
    is_reserve?: number;
    room_from?: number | null;
    room_to?: number | null;
    length_meters?: number | null;
    properties?: string | null;
  }
): Promise<DeviceInstance> => {
  const url = `/api/device_instances/${id}`;
  
  try {
    // Use clean axios instance without problematic interceptors for PUT requests
    const runtimeBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const cleanAxios = axios.create({
      baseURL: runtimeBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    const response = await cleanAxios.put(url, updates);
    
    // Apply same logic as response interceptor
    if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.success) {
      return response.data.data;
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ updateDeviceInstance error:', error);
    throw error;
  }
};

export const getDeviceInstanceById = async (id: number): Promise<DeviceInstance> => {
  const response = await apiClient.get(`/api/device-instances/${id}`);
  return response.data;
};

export interface DeviceInstanceSchemaUsage {
  schema_id: number;
  schema_name: string;
  schema_type: string | null;
  electrical_panel_id: number | null;
  panel_proj_id: string | null;
  panel_name: string | null;
}

export const getDeviceInstanceSchemas = async (instanceId: number): Promise<DeviceInstanceSchemaUsage[]> => {
  const response = await apiClient.get(`/api/device_instances/${instanceId}/schemas`);
  return Array.isArray(response.data) ? response.data : [];
};

export const deleteDeviceInstance = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/device_instances/${id}`);
};

export const getAvailableTerminalCables = async (projectId: number): Promise<DeviceInstance[]> => {
  try {
    const response = await apiClient.get(`/api/device_instances?project_id=${projectId}&device_type=cable`);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching available terminal cables:', error);
    return [];
  }
};



export const updateDeviceMainSchemaStatus = async (_deviceInstanceId: number, _onMainSchema: boolean): Promise<void> => {
  try {
    // This function is now deprecated as on_main_schema field has been removed
    console.warn('⚠️ updateDeviceMainSchemaStatus is deprecated - on_main_schema field removed');
  } catch (error) {
    console.error('❌ Error updating device main schema status:', error);
    throw error;
  }
};

export const addDeviceInstanceToSchema = async (deviceData: {
  specification_id: number;
  project_id: number;
  electrical_panel_id?: number;
  position_label?: string;
  description?: string;
}): Promise<DeviceInstance> => {
  const response = await apiClient.post('/api/device_instances', deviceData);
  return response.data;
}; 

export const getAvailableDevicesForBreakerSchema = async (projectId: number, electricalPanelId?: number, schemaId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}&not_in_schema=${schemaId}`;
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching available devices for breaker schema:', error);
    return [];
  }
};

export const getAvailableDevicesForSchemaType = async (projectId: number, schemaType: string, electricalPanelId?: number, schemaId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}&schema_type=${schemaType}`;
    if (schemaId) {
      url += `&not_in_schema=${schemaId}`;
    }
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error: any) {
    // Тихая обработка 403 ошибок (ожидаемо в режиме только просмотр)
    if (error?.response?.status === 403) {
      return [];
    }
    console.error('❌ Error fetching available devices for schema type:', error);
    return [];
  }
};

export const getAvailableCablesForBreakerSchema = async (projectId: number, electricalPanelId?: number, schemaId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}&device_type=cable&not_in_schema=${schemaId}`;
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching available cables for breaker schema:', error);
    return [];
  }
};

export const getAvailableCablesForSchemaType = async (projectId: number, schemaType: string, electricalPanelId?: number, schemaId?: number): Promise<DeviceInstance[]> => {
  try {
    let url = `/api/device_instances?project_id=${projectId}&device_type=cable&schema_type=${schemaType}`;
    if (schemaId) {
      url += `&not_in_schema=${schemaId}`;
    }
    if (electricalPanelId) {
      url += `&electrical_panel_id=${electricalPanelId}`;
    }
    
    const response = await apiClient.get(url);
    
    if (Array.isArray(response.data)) {
      return response.data as DeviceInstance[];
    }
    
    return [];
  } catch (error: any) {
    // Тихая обработка 403 ошибок (ожидаемо в режиме только просмотр)
    if (error?.response?.status === 403) {
      return [];
    }
    console.error('❌ Error fetching available cables for schema type:', error);
    return [];
  }
}; 

export const getAllProjectConnections = async (projectId: number): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/api/connections?project_id=${projectId}`);
    
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching all project connections:', error);
    return [];
  }
};

export const getDeviceTypes = async (projectId: number): Promise<string[]> => {
  try {
    const response = await apiClient.get(`/api/device_types?project_id=${projectId}`);
    
    if (Array.isArray(response.data)) {
      return response.data;
    }
    
    return [];
  } catch (error) {
    console.error('❌ Error fetching device types:', error);
    return [];
  }
};

export interface DeviceInstanceDiagramNode {
  id: number;
  specification_id: number;
  project_id: number;
  electrical_panel_id: number | null;
  instance_number: number;
  position_label: string | null;
  description: string | null;
  knx_address: string | null;
  is_reserve: number;
  room_from: string | null;
  room_to: string | null;
  length_meters: number | null;
  spec_name: string;
  device_type_id: number;
  device_type_code: string;
  device_type_name: string;
  layout_position_x: number | null;
  layout_position_y: number | null;
  layout_rotation: number | null;
  role: 'target' | 'neighbor' | 'up' | 'down';
  contacts: Array<{
    id: number;
    specification_id: number;
    contact_number: string;
    contact_position: string | null;
    contact_label: string | null;
    color_code: string | null;
  }>;
}

export interface DeviceInstanceDiagramConnection {
  id: number;
  schema_id: number;
  electrical_panel_id: number;
  from_instance_id: number;
  to_instance_id: number;
  from_contact_id: number | null;
  to_contact_id: number | null;
  connection_type: string | null;
  wire_type: string | null;
  wire_properties: string | null;
  path_points: string | null;
  is_manual_route: number;
  created_at: string;
  visibility_legacy: number;
  from_label: string | null;
  to_label: string | null;
}

export interface DeviceInstanceSchemaDiagram {
  schema_id: number;
  schema_name: string;
  schema_type: string | null;
  nodes: DeviceInstanceDiagramNode[];
  connections: DeviceInstanceDiagramConnection[];
}

export interface DeviceInstanceConnectionsResponse {
  target_id: number;
  schemas: DeviceInstanceSchemaDiagram[];
}

export const getDeviceInstanceConnections = async (instanceId: number): Promise<DeviceInstanceConnectionsResponse> => {
  const response = await apiClient.get(`/api/device_instances/${instanceId}/connections`);
  return response.data;
}; 